import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DoubleMaterialitySurveyDto,
  UpdateDoubleMaterialitySurveyDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";
import { ACTION_GET_SURVEYS } from "../GetSurveys";
import { ACTION_GET_RESULTS } from "../GetResults/useGetResults";

import { doubleMaterialitySurveysApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_SURVEY = "UPDATE_SURVEY";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type DoubleMaterialityResultsOverrides = {
  [assessmentId: string]: {
    impactLevel?: number | null;
    riskLevel?: number | null;
    opportunityLevel?: number | null;
    externalLevel?: number | null;
  };
};

type UpdateSurveyPayload = Omit<
  UpdateDoubleMaterialitySurveyDto,
  "resultsOverrides"
> & {
  resultsOverrides?: DoubleMaterialityResultsOverrides | null;
};

type UpdateSurvey = {
  surveyId: string;
  params: UpdateSurveyPayload;
};

const updateSurvey = async (
  props: UpdateSurvey,
): Promise<DoubleMaterialitySurveyDto> => {
  const apiDto = {
    ...props.params,
    resultsOverrides: props.params.resultsOverrides ?? undefined,
  };

  const res =
    await doubleMaterialitySurveysApi.doubleMaterialitySurveysControllerUpdate(
      props.surveyId,
      apiDto,
    );

  return res.data;
};


export const useUpdateSurvey = (
  user: UserProfileDto | null,
): {
  updateSurvey: (props: UpdateSurvey) => void;
  updateSurveyAsync: (props: UpdateSurvey) => Promise<DoubleMaterialitySurveyDto>;
  updateSurveyEnabled: boolean;
  isSuccessUpdateSurvey: boolean;
  isPendingUpdateSurvey: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_UPDATE_SURVEY],
    mutationFn: enabled ? updateSurvey : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_SURVEYS],
      });
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_RESULTS],
      });
    },
  });

  return {
    updateSurvey: mutate,
    updateSurveyAsync: mutateAsync,
    updateSurveyEnabled: enabled,
    isSuccessUpdateSurvey: isSuccess,
    isPendingUpdateSurvey: isPending,
  };
};
